/**
 * tm/callRec.ts — 고객 통화 녹음 공용(2026-10-28 지도사 「고객과 통화한 것도 업로드 · 모바일에서 간단하게 · 업로드 후 담당 팀장님에게 문자로 안내」)
 *  파일 이름에서 번호 · 통화 시각 → 업체 찾기 · 올리기(진행률) · 목록 · 팀장님 문자
 *  서버 routes/consult-rec(/api/rec/companies/…) · services/callRec(파일 이름 읽기 · 문자 모양은 서버와 같게)
 */
import { apiToken } from './api';
import type { Company, FP } from './types';
import { extraManagersOf } from './types';
import type { Rec } from './components/RecordingView';

/** 통화 녹음 문자 상태(서버 services/callRec) — ids = 받는 팀장님 FP id */
export interface CallNotify { state: 'pending' | 'sent' | 'partial' | 'failed' | 'off' | string; ids: string[]; results: { id: string; role?: string; name: string; sent: boolean; error?: string }[]; sent_at: string | null; error?: string }   // error = 아예 못 보낸 까닭(받을 분이 빠짐 등 · 2026-10-28)
export interface CallRec extends Rec { kind: 'call'; company_id: string; call_at: string | null; phone: string; memo: string; notify: CallNotify | null }
/** 업체의 동행상담 녹음(그 업체 상담에 올린 것 · 2026-10-29) — 업체 상세 녹음 칸에 통화와 같이 */
export interface ConsultRecRow extends Rec { kind: 'consult'; consultation_id: string; consult_date: string | null; consult_time: string; memo: string }
/** 문자 받을 담당 팀장님(서버 fpTargets) — why = 못 보내는 까닭 */
export interface CallTarget { id: string; role?: string; name: string; title: string; phone: string; why: string }

/** 번호 → 숫자만(+82 → 0) */
export function normPhone(v: string | null | undefined): string {
  let d = String(v || '').replace(/\D/g, '');
  if (d.startsWith('82') && d.length >= 11) d = '0' + d.slice(2);
  return d;
}
/** 010-1234-5678 · 02-590-4364 · 1522-6633 */
export function showPhone(v: string): string {
  const d = normPhone(v);
  if (/^02\d{7,8}$/.test(d)) return d.replace(/^(02)(\d{3,4})(\d{4})$/, '$1-$2-$3');
  if (/^0\d{9,10}$/.test(d)) return d.replace(/^(0\d{2})(\d{3,4})(\d{4})$/, '$1-$2-$3');
  if (/^1\d{7}$/.test(d)) return d.replace(/^(\d{4})(\d{4})$/, '$1-$2');
  return d;
}

/** 휴대폰 통화 녹음 파일 이름 → { phone, at, who } — 서버 services/callRec.parseCallName 과 같은 규칙 */
export function parseCallName(name: string): { phone: string; at: string; who: string } {
  const base = String(name || '').replace(/\.[a-z0-9]{2,5}$/i, '').replace(/^(통화\s*녹음|Call\s*recording)\s*/i, '');
  const iso = (y: string, mo: string, d: string, h: string, mi: string, s: string) => {
    const Y = y.length === 2 ? `20${y}` : y;
    if (+mo < 1 || +mo > 12 || +d < 1 || +d > 31 || +h > 23 || +mi > 59 || +s > 59) return '';
    return `${Y}-${mo}-${d}T${h}:${mi}:${s}+09:00`;
  };
  let phone = '', at = '', who = '';
  const m = base.match(/^(.*?)_?(\+?\d[\d-]{6,15})_(\d{2,4})(\d{2})(\d{2})_?(\d{2})(\d{2})(\d{2})$/);
  if (m) { who = m[1]; phone = normPhone(m[2]); at = iso(m[3], m[4], m[5], m[6], m[7], m[8]); }
  else {
    const n = base.match(/^(.*?)_(\d{2}|\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/);
    if (n) { who = n[1]; at = iso(n[2], n[3], n[4], n[5], n[6], n[7]); }
  }
  who = String(who || '').replace(/_/g, ' ').trim();
  if (/^[\d\s+-]*$/.test(who)) who = '';
  return { phone, at, who };
}

/** 업체 사람들 번호(대표 · 실제 대표 · 담당 · 담당 더) */
function peopleOf(c: Company): { role: string; name: string; phone: string }[] {
  return [
    { role: '대표', name: c.ceo, phone: c.ceo_phone }, { role: '실제 대표', name: c.real_ceo || '', phone: c.real_ceo_phone || '' },
    { role: '담당', name: c.manager, phone: c.manager_phone },
    ...extraManagersOf(c).map(m => ({ role: '담당', name: m.name, phone: m.phone })),
  ].map(x => ({ role: x.role, name: String(x.name || '').trim(), phone: normPhone(x.phone) })).filter(x => x.phone.length >= 8);
}

export interface CallMatch {
  phone: string; at: string; who: string;
  /** 번호가 맞는 업체(사람) — 보통 하나 */
  hits: { company: Company; role: string; name: string }[];
  /** 팀장님 번호였다면 그 팀장님(업체는 그 팀장님 업체 중에서 고름) */
  fp: FP | null;
}
/** 파일 이름 → 번호 · 시각 · 업체 찾기(번호가 같은 사람 · 없으면 이름이 같은 대표 · 담당) */
export function matchCall(fileName: string, companies: Company[], fps: FP[]): CallMatch {
  const p = parseCallName(fileName);
  const hits: CallMatch['hits'] = [];
  if (p.phone.length >= 8) companies.forEach(c => { const x = peopleOf(c).find(q => q.phone === p.phone); if (x) hits.push({ company: c, role: x.role, name: x.name }); });
  if (!hits.length && p.who) companies.forEach(c => { const x = [{ role: '대표', name: c.ceo }, { role: '실제 대표', name: c.real_ceo || '' }, { role: '담당', name: c.manager }].find(q => q.name && q.name.trim() === p.who); if (x) hits.push({ company: c, role: x.role, name: x.name }); });
  const fp = p.phone.length >= 8 ? fps.find(f => normPhone(f.phone) === p.phone) || null : null;
  return { ...p, hits, fp };
}

/** 「이정현 팀장님」 · 「김인철 WM님」 */
export function honorOf(t: { name: string; title?: string; role?: string }): string {
  return `${t.name ? t.name + ' ' : ''}${t.role === 'WM' ? 'WM' : (t.title || '').trim() || '팀장'}님`;
}
/** 문자 본문 — 서버 services/callRec.BODY 와 같은 글(2026-10-28 지도사 「고객님과 통화한 내역을 올려놓았습니다. 시간되실 때 … 업무에 참고해 주세요」) · 지도사 메모는 안 들어감 */
export const NOTICE_BODY = '고객님과 통화한 내역을 올려 두었습니다.\n시간 되실 때 확인하시고 업무에 참고해 주세요.';
/** 문자 미리보기 — 서버 services/callRec.messageFor 와 같은 꼴 */
export function noticeText(company: string, t: { name: string; title?: string; role?: string }): string {
  return [`[임지도사] ${company || '업체'} 통화 내용 안내`, '', `안녕하세요 ${honorOf(t)}`, '', NOTICE_BODY, '', '(포털 주소)'].join('\n');
}
/** 녹음 파일 보관 날수(서버 services/consultRec.KEEP_DAYS · 2026-10-28 지도사 「1달간만 보관 · 1달 뒤엔 텍스트만 · 서버비용 절감」) */
export const KEEP_DAYS = 30;
export const KEEP_NOTE = `녹음 파일은 올린 날부터 ${KEEP_DAYS}일 보관해요 · 그 뒤엔 대화 글만 남아요`;

/**
 * 통화 녹음 고르기 칸 accept(2026-10-28 지도사 「녹음 파일 바로 보여야지」 — 「작업 선택(카메라 · 캠코더 · 음성 녹음 · 파일)」 창이 먼저 떴다)
 *  안드로이드 크롬은 accept 에 소리 · 영상 종류가 하나라도 있으면(확장자 .m4a · .3gp 도 소리 · 영상으로 바꿔 봄) 녹음기 · 카메라를 함께 띄운다
 *  → 안드로이드에서는 소리 아닌 종류 둘만 넣어 「파일」 고르기 화면이 바로 열리게(모든 파일이 보임) · 고른 뒤 isCallAudio 로 녹음만 받는다
 *  아이폰 · PC 는 그대로(소리 파일만 보이게)
 */
export function callAccept(ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''): string {
  return /Android/i.test(ua) ? 'application/octet-stream,application/x-call-recording' : 'audio/*,.m4a,.amr,.aac,.mp3,.wav';
}
const CALL_EXT = /\.(m4a|amr|3gp|3ga|aac|mp3|wav|ogg|oga|flac|mp4|webm)$/i;
/** 고른 파일이 녹음인가(이름 · 종류) */
export function isCallAudio(f: { name: string; type: string }): boolean { return CALL_EXT.test(f.name) || /^audio\//.test(f.type); }

/** 통화 시각 「10/28(화) 오전 10:22」 */
export function callWhen(iso: string | null | undefined): string {
  const ms = new Date(String(iso || '')).getTime();
  if (isNaN(ms)) return '';
  const k = new Date(ms + 9 * 3600 * 1000);
  const h = k.getUTCHours(), mi = String(k.getUTCMinutes()).padStart(2, '0');
  return `${k.getUTCMonth() + 1}/${k.getUTCDate()}(${'일월화수목금토'[k.getUTCDay()]}) ${h < 12 ? '오전' : '오후'} ${h % 12 || 12}:${mi}`;
}

/* ── 서버 ── */
const API = '/api/rec';
const auth = () => ({ Authorization: 'Bearer ' + apiToken() });
export async function listCalls(companyId: string): Promise<{ list: CallRec[]; consults?: ConsultRecRow[]; targets: CallTarget[]; ready: boolean }> {
  const r = await fetch(`${API}/companies/${companyId}`, { headers: auth() });
  const j = await r.json() as { success: boolean; error?: string; data?: { list: CallRec[]; consults?: ConsultRecRow[]; targets: CallTarget[]; ready: boolean } };
  if (!j.success || !j.data) throw new Error(j.error || '불러오지 못했어요');
  return j.data;
}
export interface CallUpload { memo: string; callAt: string; phone: string; notify: boolean; fpIds: string[] }
/** 올리기(진행률) — 끝나면 { id, status, ready, notify } */
export function uploadCall(companyId: string, file: File, f: CallUpload, onPct: (n: number) => void): Promise<{ id: string; status: string; ready: boolean; notify: boolean }> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('memo', f.memo); fd.append('call_at', f.callAt); fd.append('phone', f.phone);
    fd.append('notify', f.notify && f.fpIds.length ? '1' : '0'); fd.append('ids', JSON.stringify(f.fpIds));
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/companies/${companyId}`);
    x.setRequestHeader('Authorization', 'Bearer ' + apiToken());
    x.upload.onprogress = e => { if (e.lengthComputable) onPct(Math.round((e.loaded / e.total) * 100)); };
    x.onload = () => {
      let j: { success?: boolean; error?: string; data?: { id: string; status: string; ready: boolean; notify: boolean } } = {};
      try { j = JSON.parse(x.responseText) as typeof j; } catch { /* 아래 */ }
      if (x.status === 200 && j.success && j.data) resolve(j.data); else reject(new Error(j.error || (x.status === 413 ? '파일이 너무 커요' : '올리지 못했어요')));
    };
    x.onerror = () => reject(new Error('올리지 못했어요 · 연결을 확인해 주세요'));
    x.send(fd);
  });
}
/** 통화 녹음 팀장님 문자 — later = 정리되면 보내기(이미 정리됐으면 바로) */
export async function notifyCall(recId: string, ids: string[], later = false): Promise<{ results: CallNotify['results']; state: string }> {
  const r = await fetch(`${API}/${recId}/notify`, { method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, later }) });
  const j = await r.json() as { success: boolean; error?: string; data?: { results: CallNotify['results']; state: string } };
  if (!j.success || !j.data) throw new Error(j.error || '보내지 못했어요');
  return j.data;
}
export async function recCall(method: 'PUT' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<void> {
  const r = await fetch(`${API}${path}`, { method, headers: { ...auth(), 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json() as { success: boolean; error?: string };
  if (!j.success) throw new Error(j.error || '저장하지 못했어요');
}
/** 문자 상태 한 줄 */
/** 문자 결과 알림 한 줄(PC · 휴대폰 같은 말 · 2026-10-28 — 실패도 「보냈어요」로 뜨던 것) */
export function notifyToast(state: string): string {
  return state === 'pending' ? '정리가 끝나면 문자를 보내요' : state === 'sent' ? '문자를 보냈어요' : state === 'partial' ? '일부만 보냈어요 · 결과를 봐 주세요' : '문자를 보내지 못했어요 · 이유를 봐 주세요';
}
export function notifyLine(n: CallNotify | null, targets: CallTarget[], keyReady = true): string {
  if (!n || n.state === 'off') return '';
  if (n.error && !(n.results || []).length) return `문자 못 보냄 · ${n.error}`;
  // 정리할 열쇠가 서버에 없으면 「정리되면」은 오지 않는다 — 지금 보내라고 알려 준다
  if (n.state === 'pending' && !keyReady) return '녹음 정리가 멈춰 있어 문자가 아직 안 갔어요 · 「팀장님께 문자」로 지금 보내 주세요';
  const who = (list: { name: string; title?: string; role?: string }[]) => list.map(honorOf).join(' · ');
  if (n.state === 'pending') { const ts = (n.ids || []).map(id => targets.find(t => t.id === id)).filter((t): t is CallTarget => !!t); return `정리되면 ${ts.length ? who(ts) : '팀장님'}께 문자가 가요`; }
  const full = (x: CallNotify['results'][number]) => targets.find(t => t.id === x.id) || x;   // 직함(지점장 등)은 받는 분 목록에서
  const ok = n.results.filter(x => x.sent).map(full), bad = n.results.filter(x => !x.sent);
  return [ok.length ? `${who(ok)}께 문자 보냄` : '', bad.length ? `못 보냄: ${bad.map(x => `${x.name}(${x.error || '실패'})`).join(' · ')}` : ''].filter(Boolean).join(' · ');
}

/** 휴대폰 「공유」로 받은 파일(서비스 워커 public/m-sw.js 가 share-inbox 에 넣어 둠) — 꺼내고 지움 */
export async function takeSharedFile(): Promise<File | null> {
  try {
    if (!('caches' in window)) return null;
    const c = await caches.open('share-inbox');
    const res = await c.match('/m/share-file');
    if (!res) return null;
    const blob = await res.blob();
    const name = decodeURIComponent(res.headers.get('X-File-Name') || 'call.m4a');
    await c.delete('/m/share-file');
    return new File([blob], name, { type: blob.type || 'audio/mp4' });
  } catch { return null; }
}
